import { useScrollCallback } from '@doltech/core/lib/hooks/useScrollCallback';
import { Portal } from '@reach/portal';
import cl from 'classnames';
import { isNil } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { useFollowElement } from '../../../../hooks/useFollowElement';

const SPACING = 16;

const FloatContent = styled.div`
  position: absolute;
  &.hidden {
    display: none;
  }
`;

const Main = styled.div``;

export interface SideToolbarProps extends React.HTMLAttributes<HTMLDivElement> {
  placement?: 'left' | 'right';
  containerRef?: React.MutableRefObject<HTMLDivElement>;
  spacing?: number;
  id: string;
  elementMinWidth?: number;
}

export const SideToolbar = (props: SideToolbarProps) => {
  const { elementMinWidth, children, spacing = SPACING, placement = 'left', containerRef } = props;
  const [isFocused, setIsFocused] = React.useState(false);
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  const handleScroll = () => {
    const elmToStick = containerRef.current;
    const floatElm = floatBodyRef.current;
    if (elmToStick && isFocused) {
      // const caretRect = computeCaretRect(window);
      const elmPosition = elmToStick.getBoundingClientRect();
      const floatPosition = floatElm?.getBoundingClientRect();
      let top = Math.max(elmPosition.top, 0);
      if (top + (floatPosition?.height || 0) > window.innerHeight) {
        top = window.innerHeight - (floatPosition?.height || 0);
      }
      if (placement === 'left') {
        setPosition({
          top,
          left: elmPosition.left - (elementMinWidth || 100) - spacing,
        });
      }
      if (placement === 'right') {
        setPosition({
          top,
          left: elmPosition.left + elmToStick.clientWidth + spacing,
        });
      }
    } else {
      setPosition({
        top: null,
        left: null,
      });
    }
  };

  const { scrollContainer } = useFollowElement();

  useScrollCallback(scrollContainer, handleScroll);

  React.useEffect(() => {
    const containerElm = containerRef.current;
    const onFocused = () => {
      setIsFocused(true);
    };

    const onBlur = () => {
      setIsFocused(false);
    };

    if (containerElm) {
      containerElm.addEventListener('focus', onFocused, true) as any;
      containerElm.addEventListener('blur', onBlur, true) as any;
    }
    return () => {
      if (containerElm) {
        containerElm.removeEventListener('focus', onFocused, true) as any;
        containerElm.removeEventListener('blur', onBlur, true) as any;
      }
    };
  });

  const floatBodyRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!isFocused) return;
    handleScroll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef, isFocused, placement, spacing]);

  if (!isFocused || (isNil(position.top) && isNil(position.left))) return null;
  return (
    <Main className="editor-side-toolbar">
      <Portal>
        <FloatContent
          ref={floatBodyRef}
          className={cl('editor-side-toolbar-content', { hidden: !isFocused })}
          style={position}
        >
          {children}
        </FloatContent>
      </Portal>
    </Main>
  );
};
